// src/pages/laporan/Arsip.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  FileText,
  User,
  Building2,
  Calendar,
  ChevronRight,
  Loader2,
  Archive,
} from 'lucide-react';
import { Card, Breadcrumb, Button } from '../../components/ui';
import { laporanService } from '../../services/laporan';

const risikoLabels: Record<string, { label: string; color: string }> = {
  rendah: { label: 'Rendah', color: 'bg-green-100 text-green-700' },
  sedang: { label: 'Sedang', color: 'bg-yellow-100 text-yellow-700' },
  tinggi: { label: 'Tinggi', color: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Kritis', color: 'bg-red-100 text-red-700' },
  kritis: { label: 'Kritis', color: 'bg-red-100 text-red-700' },
};

const Arsip: React.FC = () => {
  const [laporan, setLaporan] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [risikoFilter, setRisikoFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);

  const fetchArsip = async (page = currentPage) => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        page,
        per_page: 10,
        status: 'diarsipkan',
      };
      if (search) params.search = search;
      if (risikoFilter) params.tingkat_risiko = risikoFilter;

      const response = await laporanService.getAll(params);
      setLaporan(response.data || []);
      setPagination(response.meta || null);
    } catch (err) {
      console.error('Failed to load archived laporan:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArsip(currentPage);
  }, [currentPage, risikoFilter]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchArsip(1);
  };

  const getRisikoBadge = (risiko: string) => {
    const config = risikoLabels[(risiko || '').toLowerCase()] || { label: risiko, color: 'bg-gray-100 text-gray-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>{config.label}</span>;
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Arsip Laporan' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Arsip Laporan</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Daftar laporan yang sudah diarsipkan</p>
        </div>
        <Link to="/laporan">
          <Button variant="outline">Kembali ke Laporan</Button>
        </Link>
      </div>

      <Card padding="sm">
        <div className="flex items-center gap-2 text-sm text-neutral-text-secondary">
          <Archive className="w-4 h-4" />
          Data di halaman ini bersifat arsip (read-only) dan dipisahkan dari daftar laporan aktif.
        </div>
      </Card>

      <Card padding="sm">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
            <input
              type="text"
              placeholder="Cari laporan arsip..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-light"
            />
          </div>
          <select
            value={risikoFilter}
            onChange={(e) => {
              setRisikoFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="text-sm border border-neutral-border rounded-lg px-3 py-2.5 bg-white"
          >
            <option value="">Semua Risiko</option>
            <option value="rendah">Rendah</option>
            <option value="sedang">Sedang</option>
            <option value="tinggi">Tinggi</option>
            <option value="critical">Kritis</option>
          </select>
        </div>
      </Card>

      <div className="space-y-3">
        {loading ? (
          <Card className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-main mx-auto mb-3" />
            <p className="text-sm text-neutral-text-secondary">Memuat data arsip...</p>
          </Card>
        ) : laporan.length === 0 ? (
          <Card className="text-center py-12">
            <Archive className="w-12 h-12 text-neutral-text-secondary mx-auto mb-3 opacity-40" />
            <p className="text-base font-semibold text-neutral-text-primary mb-1">Belum ada laporan diarsipkan</p>
            <p className="text-sm text-neutral-text-secondary">Laporan yang sudah diarsipkan akan muncul di halaman ini.</p>
          </Card>
        ) : (
          laporan.map((lap: any) => (
            <Link key={lap.id} to={`/laporan/${lap.id}`}>
              <Card hover className="group">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-20 text-center">
                    <div className="text-xs font-mono text-teal-main font-bold">{lap.kode}</div>
                    <div
                      className="mt-1 w-3 h-3 rounded-full mx-auto"
                      style={{ backgroundColor: lap.kategori_masalah?.color || '#6B7280' }}
                      title={lap.kategori_masalah?.nama}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-neutral-text-primary truncate group-hover:text-teal-main transition-colors">
                          {lap.judul}
                        </h3>
                        <p className="text-sm text-neutral-text-secondary mt-1 line-clamp-2">
                          {lap.deskripsi || 'Tidak ada deskripsi'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Arsip</span>
                        {getRisikoBadge(lap.tingkat_risiko)}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-neutral-text-secondary">
                      <span className="flex items-center gap-1" title="Pelapor">
                        <User className="w-3.5 h-3.5" />
                        {lap.user?.name || '-'}
                      </span>
                      <span className="flex items-center gap-1" title="Unit">
                        <Building2 className="w-3.5 h-3.5" />
                        {lap.unit?.name || '-'}
                      </span>
                      <span className="flex items-center gap-1" title="Target Selesai">
                        <Calendar className="w-3.5 h-3.5" />
                        {lap.target_selesai ? new Date(lap.target_selesai).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                      </span>

                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-neutral-text-secondary group-hover:text-teal-main transition-colors shrink-0" />
                </div>
              </Card>
            </Link>
          ))
        )}
      </div>

      {pagination && pagination.last_page > 1 && (
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-text-secondary">
              Menampilkan {laporan.length} dari {pagination.total} data arsip
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-neutral-text-secondary hover:bg-neutral-bg disabled:opacity-50"
              >
                Sebelumnya
              </button>
              {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                let page: number;
                if (pagination.last_page <= 5) page = i + 1;
                else if (currentPage <= 3) page = i + 1;
                else if (currentPage >= pagination.last_page - 2) page = pagination.last_page - 4 + i;
                else page = currentPage - 2 + i;

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg ${page === currentPage ? 'bg-teal-main text-white' : 'text-neutral-text-secondary hover:bg-neutral-bg'}`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(pagination.last_page, p + 1))}
                disabled={currentPage === pagination.last_page}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-neutral-text-secondary hover:bg-neutral-bg disabled:opacity-50"
              >
                Selanjutnya
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Arsip;
